<template>
	<DataTable
		:value="masterDataTables"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		v-model:selection="selection"
		selectionMode="single"
		@row-select="onRowSelect"
		:scrollable="true"
		scrollHeight="flex"
		:rowHover="true"
		:removableSort="true"
		filterDisplay="row"
		v-model:filters="filters"
		@filter="onFilter($event)"
		@sort="onSort($event)"
		:sortField="currentSortField"
		:sortOrder="currentSortOrder"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.MasterData.MasterData') }) }}
		</template>

		<Column field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 4rem;">
			<template #body="slotProps">
				{{slotProps.data.id}}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="name" filterField="name" :header="$t('Squeeze.MasterData.Name')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.name">
					{{slotProps.data.name}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="description" filterField="description" :header="$t('Squeeze.MasterData.Description')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getTranslatedDescription(slotProps.data)">
					{{ getTranslatedDescription(slotProps.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;" v-if="!store.state.featureSet.masterDataTablePermissions && store.state.scopes.sqzAdmin">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')"
						icon="mdi mdi-delete-outline"
						class="p-button-lg p-button-rounded p-button-danger p-button-text"
						@click="openDeleteDialog(slotProps.data)"
				/>
			</template>
		</Column>

		<template #footer>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="onNewEntryClick" :disabled="!store.state.scopes.sqzMasterDataManage" />
			<Button :label="$t('Squeeze.System.Translations')" type="button" icon="mdi mdi-translate" class="p-button p-ml-2" @click="openTranslations" />
			<Button v-if="showBtnClearFilter" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters"/>
			<Button v-if="store.state.featureSet.uiConfigExport && store.state.isAdminUser" :disabled="loading || !masterDataTables.length" :label="$t('Squeeze.MasterData.Export')" type="button" icon="mdi mdi-database-export" class="p-button p-ml-2" @click="exportAllMasterData" />
			<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.ResetTableSort')" type="button" icon="mdi mdi-sort-variant-remove" class="p-button p-button-text p-ml-2" @click="resetTableSettings" />
		</template>
	</DataTable>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import {defineComponent, onMounted, PropType, ref} from "vue";
import {MasterDataTable} from "@dex/squeeze-client-ts";
import {useSqueezeStore} from "@/apps/squeeze/store";
import Tooltip from "primevue/tooltip";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {FilterMatchMode} from "primevue/api";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {useRoute} from "vue-router";
import {TableSettings} from "@/util/TableSettings";
import {ClientManager} from "@/singletons/ClientManager";

export default defineComponent({
	name: "MasterDataTableList",
	components: {
		DataTable, Column, InputText,
	},
	props: {
		masterDataTables: {
			type: Array as PropType<MasterDataTable[]>,
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onClickDelete", "onEntrySelect", "exportAllMasterData", "openTranslations",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Vuex Store */
		const store = useSqueezeStore();
		const route = useRoute();

		/** User API for persisting table settings */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current sort field */
		const currentSortField = ref<string | undefined>(undefined);

		/** Current sort order */
		const currentSortOrder = ref<number>(0);

		/** Currently-Selected row */
		const selection = ref(null);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"name": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"description": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: MasterDataTable) => {
			emit("onClickDelete", row);
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", 0)
		}

		/** Triggered when a row is selected */
		const onRowSelect = (event: any) => {
			emit("onEntrySelect", event.data.id)
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			if (event.filteredValue.length < props.masterDataTables!.length) {
				showBtnClearFilter.value = true;
			} else {
				showBtnClearFilter.value = false;
			}
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		initFilters();

		/** Export all master data */
		const exportAllMasterData = () => {
			emit("exportAllMasterData");
		}

		/** Opens the translations page for all master data translation keys */
		const openTranslations = () => {
			emit("openTranslations");
		}

		/** Triggered on sort a column */
		const onSort = (event: { sortField: string; sortOrder: number }) => {
			currentSortField.value = event.sortField;
			currentSortOrder.value = event.sortOrder;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', '', { field: event.sortField, order: event.sortOrder }, v => !v || v.order === 0);
		}

		/** Reset sort settings */
		const resetTableSettings = () => {
			currentSortField.value = undefined;
			currentSortOrder.value = 0;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', '', { field: '', order: 0 }, v => !v || v.order === 0);
		}

		onMounted(() => {
			const savedSort = TableSettings.getTableSubSetting<{ field: string; order: number }>(store, route.name as string, 'sort', '');
			if (savedSort) {
				currentSortField.value = savedSort.field;
				currentSortOrder.value = savedSort.order;
			}
		});

		return {
			getTranslatedDescription,
			t, toast, store, DataTable, Column, filters, showBtnClearFilter, initFilters, openDeleteDialog, onRowSelect, selection, onNewEntryClick, onFilter, clearFilters, exportAllMasterData, openTranslations,
			currentSortField, currentSortOrder, onSort, resetTableSettings,
		}
	},
});
</script>

<style lang="scss" scoped>

::v-deep(.p-datatable-tbody tr) {
	height: 3rem;
}

::v-deep(td) {
	white-space: nowrap;
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

// Table header
.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
   background-color: var(--dex-primary-light-color);
   padding: 0.6rem 0.6rem 0.2rem 0.3rem;
   border-bottom-color: var(--dex-primary-light-color);
   color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

</style>
